import React, { createContext, useContext, useState } from 'react';
import { Snackbar, Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

// Create the context
const ToastContext = createContext(null);

// Styled Toast Container
const ToastContainer = styled(Box)(({ theme, severity }) => {
  const colors = {
    success: {
      bg: '#E8F5E9', // Light green background
      iconBg: '#4CAF50', // Dark green icon background
      text: '#2E7D32', // Dark green text
      closeIcon: '#616161' // Dark grey close icon
    },
    error: {
      bg: '#FFEBEE', // Light red background
      iconBg: '#F44336', // Dark red icon background
      text: '#C62828', // Dark red text
      closeIcon: '#616161' // Dark grey close icon
    },
    warning: {
      bg: '#FFF3E0', // Light orange background
      iconBg: '#FF9800', // Orange icon background
      text: '#E65100', // Dark orange text
      closeIcon: '#616161'
    },
    info: {
      bg: '#E3F2FD', // Light blue background
      iconBg: '#2196F3', // Blue icon background
      text: '#1565C0', // Dark blue text
      closeIcon: '#616161'
    }
  };

  const colorScheme = colors[severity] || colors.success;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: colorScheme.bg,
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    minWidth: '300px',
    maxWidth: '500px',
    position: 'relative',
    '& .toast-icon': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: colorScheme.iconBg,
      color: '#FFFFFF',
      flexShrink: 0,
      '& svg': {
        fontSize: '16px'
      }
    },
    '& .toast-message': {
      flex: 1,
      color: colorScheme.text,
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.5
    },
    '& .toast-close': {
      color: colorScheme.closeIcon,
      padding: '4px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)'
      }
    }
  };
});

// Custom Toast Component
const CustomToast = ({ open, message, severity, onClose }) => {
  const icons = {
    success: <CheckCircleIcon />,
    error: <ErrorIcon />,
    warning: <WarningIcon />,
    info: <InfoIcon />
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        '& .MuiSnackbar-root': {
          top: '24px !important'
        }
      }}
    >
      <ToastContainer severity={severity}>
        <Box className="toast-icon">
          {icons[severity] || icons.success}
        </Box>
        <Typography className="toast-message">
          {message}
        </Typography>
        <IconButton
          size="small"
          className="toast-close"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </ToastContainer>
    </Snackbar>
  );
};

// ToastProvider component
export function ToastProvider({ children }) {
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const hideToast = () => {
    setToast({ ...toast, open: false });
  };

  const value = {
    showSuccess: (message) => showToast(message, 'success'),
    showError: (message) => showToast(message, 'error'),
    showWarning: (message) => showToast(message, 'warning'),
    showInfo: (message) => showToast(message, 'info'),
    // Helper methods for common operations
    showAddSuccess: (itemName = 'Item') => showToast(`${itemName} added successfully`, 'success'),
    showAddError: (itemName = 'Item') => showToast(`Failed to add ${itemName.toLowerCase()}`, 'error'),
    showUpdateSuccess: (itemName = 'Item') => showToast(`${itemName} updated successfully`, 'success'),
    showUpdateError: (itemName = 'Item') => showToast(`Failed to update ${itemName.toLowerCase()}`, 'error'),
    showDeleteSuccess: (itemName = 'Item') => showToast(`${itemName} deleted successfully`, 'success'),
    showDeleteError: (itemName = 'Item') => showToast(`Failed to delete ${itemName.toLowerCase()}`, 'error')
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <CustomToast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </ToastContext.Provider>
  );
}

// Custom hook for using toast
export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
